import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CASH_CHANGE = 20;

// Scan/enter a gift voucher and add it as a payment. remaining = amount still due.
export const GiftVoucherTender = ({ remaining, usedCodes, onAdd }) => {
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOutChange, setPayOutChange] = useState(true);

  const check = async () => {
    const c = code.trim();
    if (!c) return;
    if (usedCodes.includes(c)) return toast.error("This voucher is already added");
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/gift-vouchers/lookup/${encodeURIComponent(c)}`);
      if (data.status !== "ACTIVE") {
        setVoucher(null);
        return toast.error(`Voucher is ${data.status.toLowerCase()}`);
      }
      setVoucher(data);
    } catch (e) {
      setVoucher(null);
      toast.error(e?.response?.data?.detail || "Voucher not found");
    } finally {
      setBusy(false);
    }
  };

  const use = voucher ? Math.min(voucher.balance, Math.max(0, remaining)) : 0;
  const leftover = voucher ? Math.round((voucher.balance - use) * 100) / 100 : 0;
  const canPayOut = leftover > 0 && leftover < MAX_CASH_CHANGE && use >= remaining - 0.009;

  const add = () => {
    if (use <= 0) return toast.error("Nothing left to pay");
    onAdd({
      method: "GIFT_VOUCHER",
      amount: Math.round(use * 100) / 100,
      reference: voucher.code,
      cash_change: canPayOut && payOutChange ? leftover : 0,
    });
    setVoucher(null);
    setCode("");
  };

  return (
    <div className="space-y-2" data-testid="gift-voucher-tender">
      <div className="flex gap-2">
        <Input autoFocus value={code} onChange={(e) => { setCode(e.target.value); setVoucher(null); }}
          onKeyDown={(e) => e.key === "Enter" && check()} placeholder="Scan or type voucher code"
          className="h-10 font-mono" data-testid="gv-code-input" />
        <Button type="button" onClick={check} disabled={busy} className="h-10" data-testid="gv-check-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
        </Button>
      </div>
      {voucher && (
        <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-2 text-sm" data-testid="gv-tender-details">
          <p className="flex items-center gap-1 font-semibold"><Gift className="h-4 w-4 text-rose-600" /> Balance R {voucher.balance.toFixed(2)}</p>
          <p className="text-xs text-slate-600">Valid until {new Date(voucher.expires_at).toLocaleDateString("en-ZA")}</p>
          <p className="mt-1">Use now: <b>R {use.toFixed(2)}</b>{leftover > 0 && !(canPayOut && payOutChange) && <> · R {leftover.toFixed(2)} stays on voucher</>}</p>
          {canPayOut && (
            <label className="mt-1 flex items-center gap-2 cursor-pointer">
              <Checkbox checked={payOutChange} onCheckedChange={(v) => setPayOutChange(!!v)} data-testid="gv-payout-change" />
              Give R {leftover.toFixed(2)} leftover as cash change
            </label>
          )}
          <Button type="button" className="mt-2 w-full h-9 bg-rose-600 hover:bg-rose-700" onClick={add} data-testid="gv-add-payment-btn">
            Pay R {use.toFixed(2)} with voucher
          </Button>
        </div>
      )}
    </div>
  );
};
